import { ExternalLink, Pencil, Reply, SmilePlus, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { getFileIcon } from '../FileUpload';
import ImageLightbox from './ImageLightbox';
import MessageTicks from './MessageTicks';
import { whatsappTheme } from './whatsappTheme';

const { colors } = whatsappTheme;

const URL_RE = /(https?:\/\/[^\s]+)/g;

/** Bare URLs (e.g. the Google Maps link in a shared location) become real links; everything else
 * stays plain text - React never interprets it as markup, so this can't introduce HTML injection. */
const linkify = (text) => {
  if (typeof text !== 'string' || !text) return text;
  return text.split(URL_RE).map((part, i) =>
    i % 2 === 1 ? (
      <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="underline break-all">
        {part}
      </a>
    ) : (
      part
    ),
  );
};

/** The small pointed corner on the first bubble of a run. Pure decoration, so hidden from readers. */
const Tail = ({ outgoing }) => (
  <svg
    data-testid="tail"
    aria-hidden="true"
    viewBox="0 0 8 13"
    width="8"
    height="13"
    className={`absolute top-0 ${outgoing ? '-right-[8px]' : '-left-[8px]'}`}
    style={{ color: outgoing ? colors.outgoingBubble : colors.incomingBubble }}
  >
    <path fill="currentColor" d={outgoing ? 'M0 0h8L0 13z' : 'M8 0H0l8 13z'} />
  </svg>
);

const PENDING_LABELS = { image: 'Photo', video: 'Video', audio: 'Voice message', document: 'Document', sticker: 'Sticker' };

/** A media message whose file hasn't been downloaded yet (nothing is fetched until opened, like
 * WhatsApp itself). Tapping it fetches and caches it on the backend; later opens are instant. */
const PendingMedia = ({ messageType, filename, onFetch, onLoaded }) => {
  const [state, setState] = useState('idle'); // idle | loading | error

  const load = async () => {
    if (!onFetch) return;
    setState('loading');
    try {
      const { url, mime_type } = await onFetch();
      onLoaded(url, mime_type);
    } catch {
      setState('error');
    }
  };

  const label = PENDING_LABELS[messageType] || 'Media';
  const text =
    state === 'loading'
      ? 'Loading…'
      : state === 'error'
        ? "Couldn't load — tap to retry"
        : `${label}${filename ? `: ${filename}` : ''} — tap to load`;

  return (
    <button
      type="button"
      onClick={load}
      disabled={state === 'loading'}
      className="mb-1 flex w-full items-center gap-2 rounded-[6px] bg-black/5 px-2 py-2 text-left disabled:opacity-70"
    >
      {getFileIcon(filename || label)}
      <span className="min-w-0 break-all">{text}</span>
    </button>
  );
};

/** The media part of a bubble, chosen from the mime type. Images open in an in-app viewer. */
const Media = ({ url, mimeType, filename, pending, messageType, onFetch }) => {
  const [viewing, setViewing] = useState(false);
  const [fetched, setFetched] = useState(/** @type {{url: string, mimeType: string} | null} */ (null));

  if (pending && !fetched) {
    return (
      <PendingMedia
        messageType={messageType}
        filename={filename}
        onFetch={onFetch}
        onLoaded={(fetchedUrl, fetchedMime) => setFetched({ url: fetchedUrl, mimeType: fetchedMime })}
      />
    );
  }

  const effectiveUrl = fetched?.url ?? url;
  const type = fetched?.mimeType ?? mimeType ?? '';
  if (type.startsWith('image/')) {
    return (
      <>
        <button
          type="button"
          aria-label="Open photo"
          onClick={() => setViewing(true)}
          className="mb-1 block cursor-zoom-in"
        >
          <img
            src={effectiveUrl}
            alt={filename || 'Photo'}
            loading="lazy"
            className="max-h-64 max-w-full rounded-[6px] object-cover"
          />
        </button>
        {viewing && <ImageLightbox url={effectiveUrl} alt={filename || 'Photo'} onClose={() => setViewing(false)} />}
      </>
    );
  }
  if (type.startsWith('video/')) {
    return <video controls preload="metadata" src={effectiveUrl} className="mb-1 max-h-64 max-w-full rounded-[6px]" />;
  }
  if (type.startsWith('audio/')) {
    return <audio controls preload="metadata" src={effectiveUrl} className="mb-1 max-w-full" />;
  }
  let urlName = '';
  try {
    urlName = decodeURIComponent(String(effectiveUrl).split('?')[0].split('/').pop() || '');
  } catch {
    urlName = '';
  }
  const name = filename || urlName || 'Document';
  return (
    <a
      href={effectiveUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="mb-1 flex items-center gap-2 rounded-[6px] bg-black/5 px-2 py-2 underline-offset-2 hover:underline"
    >
      {getFileIcon(name)}
      <span className="min-w-0 break-all">{name}</span>
    </a>
  );
};

/** The WhatsApp-style "replying to" block at the top of a reply: coloured left bar, sender, and a
 * one-or-two-line preview. Clickable (jumps to the original) only when that message is on the page. */
const QuoteBlock = ({ quote, onClick }) => {
  const accent = quote.sender === 'You' ? colors.header : colors.link;
  const body = (
    <>
      {quote.sender && (
        <span className="block truncate text-[12.8px] font-medium leading-[18px]" style={{ color: accent }}>
          {quote.sender}
        </span>
      )}
      <span
        className={`line-clamp-2 block break-words text-[13px] leading-[18px] ${quote.sender ? '' : 'italic'}`}
        style={{ color: colors.textSecondary }}
      >
        {quote.text}
      </span>
    </>
  );
  const className = 'mb-1 block w-full overflow-hidden rounded-[6px] border-l-4 bg-black/[0.06] px-2 py-1 text-left';
  return onClick ? (
    <button
      type="button"
      data-testid="quote"
      aria-label="Go to quoted message"
      onClick={onClick}
      className={`${className} cursor-pointer hover:bg-black/10`}
      style={{ borderLeftColor: accent }}
    >
      {body}
    </button>
  ) : (
    <div data-testid="quote" className={className} style={{ borderLeftColor: accent }}>
      {body}
    </div>
  );
};

/** The WhatsApp-style link card (thumbnail, title, description, domain) shown above the text for a
 * click-to-WhatsApp ad. The whole card opens the link. The ad CDN's signed thumbnail URLs expire, so
 * without a (working) image the card keeps a plain tile with the post/reel link, and the product name
 * matched from the ad copy, so the context outlives the image. */
const LinkPreviewCard = ({ preview }) => {
  const [imageFailed, setImageFailed] = useState(false);
  if (!preview?.url || !/^https:\/\//i.test(preview.url)) return null;
  const showImage = preview.image_url && /^https:\/\//i.test(preview.image_url) && !imageFailed;
  // With the thumbnail the domain is enough; without it the card is the only record, so show the full link.
  const footer = showImage ? preview.domain : preview.url.replace(/^https:\/\/(www\.)?/i, '');
  return (
    <a
      data-testid="link-preview"
      href={preview.url}
      target="_blank"
      rel="noopener noreferrer"
      className="mb-1 block overflow-hidden rounded-[6px] bg-black/[0.06] hover:bg-black/10"
    >
      {!showImage && (
        <span
          data-testid="link-preview-fallback"
          className="flex items-center gap-1 px-2 pt-1 text-[12px] font-medium leading-[18px]"
          style={{ color: colors.link }}
        >
          <ExternalLink aria-hidden="true" size={13} />
          <span className="min-w-0 truncate">{preview.media_type === 'video' ? 'Open reel / video' : 'Open post'}</span>
        </span>
      )}
      {showImage && (
        <img
          src={preview.image_url}
          alt=""
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setImageFailed(true)}
          className="max-h-[200px] w-full object-cover"
        />
      )}
      <span className="block px-2 py-1">
        {preview.product && (
          <span data-testid="link-preview-product" className="block break-words text-[13.5px] font-semibold leading-[18px]">
            {preview.product}
          </span>
        )}
        {preview.title && (
          <span className="line-clamp-2 block break-words text-[13.5px] font-medium leading-[18px]">{preview.title}</span>
        )}
        {preview.description && (
          <span className="line-clamp-3 block break-words text-[13px] leading-[18px]" style={{ color: colors.textSecondary }}>
            {preview.description}
          </span>
        )}
        {footer && (
          <span className="block truncate text-[12px] leading-[18px]" style={{ color: colors.textSecondary }}>
            {footer}
          </span>
        )}
      </span>
    </a>
  );
};

const QUICK_EMOJIS = ['\u{1F44D}', '❤️', '\u{1F602}', '\u{1F62E}', '\u{1F622}', '\u{1F64F}'];

/** The small emoji strip that opens from the React hover button. */
const ReactionPicker = ({ outgoing, onPick, onClose }) => {
  const ref = useRef(null);
  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [onClose]);
  return (
    <div
      ref={ref}
      role="menu"
      aria-label="React"
      className={`absolute bottom-full z-10 mb-1 flex gap-1 rounded-full border border-gray-200 bg-white px-2 py-1 shadow-lg ${outgoing ? 'right-0' : 'left-0'}`}
    >
      {QUICK_EMOJIS.map((emoji) => (
        <button
          key={emoji}
          type="button"
          role="menuitem"
          onClick={() => onPick(emoji)}
          className="text-base leading-none hover:scale-125"
        >
          {emoji}
        </button>
      ))}
    </div>
  );
};

/** Reply / React / Edit / Delete, shown on hover like WhatsApp Web. Hidden entirely when none are wired up. */
const BubbleActions = ({ outgoing, onReply, onReact, onEdit, onDelete }) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  if (!onReply && !onReact && !onEdit && !onDelete) return null;
  return (
    <div
      className={`absolute top-0 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 ${outgoing ? 'right-full mr-1' : 'left-full ml-1'}`}
    >
      {onReact && (
        <div className="relative">
          <button
            type="button"
            aria-label="React"
            onClick={() => setPickerOpen((open) => !open)}
            className="rounded-full p-1 text-gray-500 hover:bg-black/10"
          >
            <SmilePlus size={15} />
          </button>
          {pickerOpen && (
            <ReactionPicker
              outgoing={outgoing}
              onClose={() => setPickerOpen(false)}
              onPick={(emoji) => {
                setPickerOpen(false);
                onReact(emoji);
              }}
            />
          )}
        </div>
      )}
      {onReply && (
        <button type="button" aria-label="Reply" onClick={onReply} className="rounded-full p-1 text-gray-500 hover:bg-black/10">
          <Reply size={15} />
        </button>
      )}
      {onEdit && (
        <button type="button" aria-label="Edit" onClick={onEdit} className="rounded-full p-1 text-gray-500 hover:bg-black/10">
          <Pencil size={15} />
        </button>
      )}
      {onDelete && (
        <button type="button" aria-label="Delete" onClick={onDelete} className="rounded-full p-1 text-gray-500 hover:bg-black/10">
          <Trash2 size={15} />
        </button>
      )}
    </div>
  );
};

/** The small emoji pill overlapping the bubble's bottom corner, mirroring real WhatsApp - a
 * reaction is never its own message, just a badge on the message it reacts to. */
const ReactionBadge = ({ reactions }) => {
  const emojis = [reactions?.in, reactions?.out].filter(Boolean);
  if (!emojis.length) return null;
  return (
    <div
      data-testid="reaction-badge"
      className="absolute -bottom-2 right-1 flex items-center gap-0.5 rounded-full border border-black/5 bg-white px-1 py-0.5 text-[11px] leading-none shadow"
    >
      {emojis.map((emoji, i) => (
        <span key={i}>{emoji}</span>
      ))}
    </div>
  );
};

/**
 * One WhatsApp message bubble. Outgoing sits right in green, incoming left in white. `tail` marks the
 * first bubble of a run; `below` renders extra rows (template buttons) under the bubble, same width.
 * `onReply`/`onReact`/`onEdit`/`onDelete` show a hover toolbar (WhatsApp Web style); omit any to hide
 * that action. `deleted` renders a muted "message was deleted" placeholder instead of content/media/
 * reactions/hover actions - this is a local-only tombstone, never a real WhatsApp unsend.
 * @param {{direction: 'in' | 'out', children: import('react').ReactNode, time?: string, status?: string | null, tail?: boolean, below?: import('react').ReactNode, media?: {url: string | null, mimeType?: string | null, filename?: string | null, pending?: boolean, messageType?: string, onFetch?: () => Promise<{url: string, mime_type: string}>} | null, reactions?: {in?: string, out?: string} | null, quote?: {id: string | null, sender: string | null, text: string} | null, linkPreview?: {url: string, domain?: string | null, title?: string | null, description?: string | null, image_url?: string | null, product?: string | null, media_type?: string | null} | null, onQuoteClick?: () => void, deleted?: boolean, onReply?: () => void, onReact?: (emoji: string) => void, onEdit?: () => void, onDelete?: () => void}} props
 */
const ChatBubble = ({
  direction,
  children,
  time,
  status,
  tail = false,
  below = null,
  media = null,
  reactions = null,
  quote = null,
  linkPreview = null,
  onQuoteClick,
  deleted = false,
  onReply,
  onReact,
  onEdit,
  onDelete,
}) => {
  const outgoing = direction === 'out';
  const corner = tail ? (outgoing ? 'rounded-tr-none' : 'rounded-tl-none') : '';
  const showMedia = !deleted && (media?.pending || (media?.url && /^https:\/\//i.test(media.url)));
  return (
    <div className={`flex w-full ${outgoing ? 'justify-end' : 'justify-start'}`}>
      <div className="group relative mb-1 flex max-w-[85%] flex-col">
        {tail && <Tail outgoing={outgoing} />}
        {!deleted && (
          <BubbleActions
            outgoing={outgoing}
            onReply={onReply}
            onReact={onReact}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        )}
        <div
          data-testid="bubble"
          className={`rounded-[7.5px] px-[9px] pb-[6px] pt-[6px] text-[14.2px] leading-[19px] shadow-[0_1px_0.5px_rgba(11,20,26,0.13)] ${corner}`}
          style={{
            backgroundColor: outgoing ? colors.outgoingBubble : colors.incomingBubble,
            color: deleted ? colors.textSecondary : colors.textPrimary,
          }}
        >
          {quote && (
            <QuoteBlock quote={quote} onClick={quote.id ? onQuoteClick : undefined} />
          )}
          {!deleted && linkPreview && <LinkPreviewCard preview={linkPreview} />}
          {showMedia && (
            <Media
              url={media.url}
              mimeType={media.mimeType}
              filename={media.filename}
              pending={media.pending}
              messageType={media.messageType}
              onFetch={media.onFetch}
            />
          )}
          <div className="flow-root">
            <div data-testid="content" className={`whitespace-pre-wrap break-words ${deleted ? 'italic' : ''}`}>
              {deleted ? children : linkify(children)}
            </div>
            <span className="float-right -mb-[2px] ml-3 mt-[3px] flex items-center gap-[3px]">
              {time && (
                <time className="text-[11px] leading-none" style={{ color: colors.textSecondary }}>
                  {time}
                </time>
              )}
              {outgoing && <MessageTicks status={status} />}
            </span>
          </div>
        </div>
        {!deleted && <ReactionBadge reactions={reactions} />}
        {below}
      </div>
    </div>
  );
};

export default ChatBubble;
